import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useExitOfferStore } from '@/store/exitOfferStore';
import { useUserStore } from '@/store/userStore';
import { apiService, PRO_PLAN, ExitOfferExpiredError } from '@/api';
import { Button } from '@/components/ui';
import { cn } from '@/utils/cn';
import { X, Clock, Gift, AlertCircle } from 'lucide-react';
import {
  CheckoutTrustStrip,
  PlanFeatureBullets,
  ReviewsSection,
  FAQS,
  FaqItem,
} from '@/v2/pages/UpgradePage';
import {
  trackBeginCheckout,
  trackExitOfferShown,
  trackExitOfferClicked,
  trackExitOfferDismissed,
  trackExitOfferExpired,
} from '@/services/analyticsService';
import { getEmailAttribution } from '@/services/emailAttribution';

const generateTransactionId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// One-time injected keyframes for the digit roll (mm/ss cells) + the last-minute
// pulse. Kept local so the component owns its animation with no global CSS.
const TIMER_STYLE_ID = 'exit-offer-timer-style';
function ensureTimerStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TIMER_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = TIMER_STYLE_ID;
  el.textContent = `
@keyframes exitOfferDigitRoll {
  0% { transform: translateY(-55%); opacity: 0; }
  60% { opacity: 1; }
  100% { transform: translateY(0); opacity: 1; }
}
@keyframes exitOfferUrgentPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
.exit-offer-digit { display: inline-block; animation: exitOfferDigitRoll 260ms cubic-bezier(0.22,1,0.36,1); }
.exit-offer-urgent { animation: exitOfferUrgentPulse 1s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .exit-offer-digit, .exit-offer-urgent { animation: none !important; }
}`;
  document.head.appendChild(el);
}

type Segments = { hrs: string; hourMins: string; mins: string; secs: string; cs: string; totalMs: number };

function computeSegments(expiresAt: number): Segments {
  const totalMs = Math.max(0, expiresAt - Date.now());
  const totalSeconds = Math.floor(totalMs / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const cs = Math.floor((totalMs % 1000) / 10);
  return {
    hrs: Math.floor(mins / 60).toString().padStart(2, '0'),
    hourMins: (mins % 60).toString().padStart(2, '0'),
    mins: mins.toString().padStart(2, '0'),
    secs: secs.toString().padStart(2, '0'),
    cs: cs.toString().padStart(2, '0'),
    totalMs,
  };
}

/**
 * Self-contained rAF countdown so only this subtree re-renders (not the whole
 * overlay) ~60x/sec. The mm/ss cells roll when they change; the hundredths tick
 * continuously. `expiresAt` is the absolute server deadline, so the display stays
 * correct across reloads/tab-switches without any drift correction.
 *
 * NOTE: the cells are rendered as plain JSX (not nested components) so that the
 * 60fps parent re-render does NOT remount them — the roll animation is driven
 * purely by the `key={value}` on the digit span, so it replays only when a digit
 * actually changes (an earlier version defined the cells as inline components,
 * which React treated as a new type every frame → constant remount → the roll
 * animation restarted every frame and the digits appeared blank).
 */
// format 'hms' (hh:mm:ss) is for multi-hour deadlines such as the recovery-email offers.
export function Countdown({
  expiresAt,
  size,
  onExpire,
  format = 'msc',
}: {
  expiresAt: number;
  size: 'sm' | 'lg';
  onExpire: () => void;
  format?: 'msc' | 'hms';
}) {
  const [seg, setSeg] = useState<Segments>(() => computeSegments(expiresAt));
  const firedExpire = useRef(false);

  useEffect(() => {
    ensureTimerStyle();
    let raf = 0;
    const tick = () => {
      const next = computeSegments(expiresAt);
      setSeg(next);
      if (next.totalMs <= 0) {
        if (!firedExpire.current) {
          firedExpire.current = true;
          onExpire();
        }
        return; // stop the loop at zero
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [expiresAt, onExpire]);

  const urgent = seg.totalMs <= 60_000;
  const reduce = prefersReducedMotion();
  const cell = size === 'lg'
    ? 'px-2 py-1 rounded-lg text-base min-w-[34px] h-8'
    : 'px-1.5 py-0.5 rounded text-xs min-w-[26px] h-6';
  const cellCls = cn(
    cell,
    'inline-flex items-center justify-center text-center font-mono font-bold overflow-hidden leading-none',
    urgent ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-white',
  );
  const colonCls = cn(size === 'lg' ? 'font-bold' : 'font-bold text-xs', urgent ? 'text-red-400/70' : 'text-white/50');

  if (format === 'hms') {
    return (
      <div className={cn('flex items-center gap-1', urgent && !reduce && 'exit-offer-urgent')}>
        <span className={cellCls}>
          <span key={seg.hrs} className={reduce ? undefined : 'exit-offer-digit'}>{seg.hrs}</span>
        </span>
        <span className={colonCls}>:</span>
        <span className={cellCls}>
          <span key={seg.hourMins} className={reduce ? undefined : 'exit-offer-digit'}>{seg.hourMins}</span>
        </span>
        <span className={colonCls}>:</span>
        <span className={cellCls}>
          <span key={seg.secs} className={reduce ? undefined : 'exit-offer-digit'}>{seg.secs}</span>
        </span>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-1', urgent && !reduce && 'exit-offer-urgent')}>
      {/* mm — rolls on change (keyed by value) */}
      <span className={cellCls}>
        <span key={seg.mins} className={reduce ? undefined : 'exit-offer-digit'}>{seg.mins}</span>
      </span>
      <span className={colonCls}>:</span>
      {/* ss — rolls on change */}
      <span className={cellCls}>
        <span key={seg.secs} className={reduce ? undefined : 'exit-offer-digit'}>{seg.secs}</span>
      </span>
      <span className={colonCls}>:</span>
      {/* hundredths — tick continuously, never animated */}
      <span className={cellCls}>{seg.cs}</span>
    </div>
  );
}

export function ExitOffer() {
  const { t } = useTranslation('v2');
  const { showOverlay, showBanner, expiresAt, dismissOverlay, endOffer } = useExitOfferStore();
  const user = useUserStore((s) => s.user);
  const userId = useUserStore((s) => s.userId);

  const [overlayVisible, setOverlayVisible] = useState(false);
  const [closeVisible, setCloseVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shownTracked = useRef(false);

  // Slide-up + delayed close button (mirrors the previous overlay choreography).
  useEffect(() => {
    if (showOverlay) {
      const mountTimer = setTimeout(() => setOverlayVisible(true), 50);
      setCloseVisible(false);
      const closeTimer = setTimeout(() => setCloseVisible(true), 4000);
      if (!shownTracked.current) {
        shownTracked.current = true;
        trackExitOfferShown();
      }
      return () => { clearTimeout(mountTimer); clearTimeout(closeTimer); };
    }
    setOverlayVisible(false);
    setCloseVisible(false);
  }, [showOverlay]);

  const handleExpire = useCallback(() => {
    trackExitOfferExpired();
    endOffer();
  }, [endOffer]);

  const handleSubscribe = async (surface: 'overlay' | 'banner', e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!userId || isLoading) return;
    setIsLoading(true);
    setError(null);
    trackExitOfferClicked(surface);
    trackBeginCheckout('pro_weekly_exit_499', PRO_PLAN.exitFirstWeekAmount);
    try {
      const txnId = generateTransactionId();
      const email = user?.email || undefined;
      const { checkoutUrl } = await apiService.createCheckoutSession(
        userId,
        'weekly',
        email,
        undefined,
        `${window.location.origin}/subscription?success=true&txn_id=${txnId}&plan=pro&period=weekly`,
        `${window.location.origin}/subscription?canceled=true&plan=pro`,
        false,
        PRO_PLAN.weeklyPriceId,
        undefined,
        undefined,
        undefined,
        undefined,
        'exit_first_week',
        getEmailAttribution(),
      );
      if (checkoutUrl) window.location.href = checkoutUrl;
      else setError(t('exitOffer.error'));
    } catch (err) {
      if (err instanceof ExitOfferExpiredError) {
        endOffer();
        setError(t('exitOffer.expired'));
      } else {
        setError(t('exitOffer.error'));
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    trackExitOfferDismissed();
    dismissOverlay();
  };

  if (!showOverlay && !showBanner) return null;
  if (!expiresAt || Date.now() >= expiresAt) return null;

  const weekly = PRO_PLAN.weeklyAmount;
  const first = PRO_PLAN.exitFirstWeekAmount;

  return (
    <>
      {/* ===== Persistent banner (after the overlay is dismissed) ===== */}
      {showBanner && !showOverlay && (
        <div
          onClick={() => handleSubscribe('banner')}
          className="fixed top-0 left-0 right-0 z-[65] bg-slate-900 border-b border-emerald-500/20 animate-fade-in cursor-pointer hover:brightness-110 transition-all"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 md:gap-5 flex-wrap min-w-0">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-white whitespace-nowrap">{t('exitOffer.limitedOffer')}</span>
              </div>
              <span className="text-emerald-400 font-bold text-sm whitespace-nowrap">
                {t('exitOffer.firstWeekPrice', { price: first })}
              </span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-slate-400 text-xs line-through">${weekly}{t('exitOffer.perWeekShort')}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 text-xs whitespace-nowrap"
                onClick={(e) => handleSubscribe('banner', e)}
                isLoading={isLoading}
              >
                {t('exitOffer.claimCta')}
              </Button>
              <div className="hidden sm:block">
                <Countdown expiresAt={expiresAt} size="sm" onExpire={handleExpire} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Full-screen overlay (slides up from bottom) ===== */}
      {showOverlay && (
        <div className="fixed inset-0 z-[60]">
          <div className={cn('absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500', overlayVisible ? 'opacity-100' : 'opacity-0')} />
          <div className={cn('absolute inset-0 bg-background overflow-y-auto transition-transform duration-700 ease-out', overlayVisible ? 'translate-y-0' : 'translate-y-full')}>
            {closeVisible && (
              <button onClick={handleClose} className="absolute top-4 left-4 z-[65] p-2 rounded-full bg-surface-light border border-glass-border hover:bg-glass-border transition-colors animate-fade-in" aria-label={t('exitOffer.close')}>
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            )}

            <div className="max-w-2xl mx-auto px-4 pt-12 pb-12">
              <div className="text-center mb-4">
                <h1 className="text-2xl md:text-3xl font-bold text-text-primary">{t('exitOffer.headline', { price: first })}</h1>
                <p className="text-sm text-text-secondary mt-2">{t('exitOffer.subhead', { price: first, weekly })}</p>
              </div>

              {/* Timer */}
              <div className="flex justify-center mb-8">
                <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl bg-slate-900 border border-emerald-500/30">
                  <Clock className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-300">{t('exitOffer.expiresIn')}</span>
                  <Countdown expiresAt={expiresAt} size="lg" onExpire={handleExpire} />
                </div>
              </div>

              {/* Plan card — same structure/sections as the real paywall Pro card */}
              <div className="mb-8">
                <div
                  onClick={() => !isLoading && handleSubscribe('overlay')}
                  className="relative rounded-2xl border-2 border-emerald-500/40 bg-surface flex flex-col cursor-pointer transition-all duration-200 hover:border-emerald-500/60"
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold">
                      {t('exitOffer.badge')}
                    </span>
                  </div>

                  <div className="p-6 md:p-8 flex-1 flex flex-col">
                    <div className="mb-5">
                      <h3 className="text-2xl font-bold text-text-primary mb-1">{t('exitOffer.planName')}</h3>
                      <p className="text-sm text-text-secondary min-h-10">{t('exitOffer.planDescription')}</p>
                    </div>

                    <div className="mb-5">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl text-text-tertiary line-through">${weekly}</span>
                        <span className="text-4xl font-bold text-emerald-500">${first}</span>
                        <span className="text-text-secondary text-sm">{t('exitOffer.firstWeekLabel')}</span>
                      </div>
                      <p className="text-xs text-text-secondary mt-1.5">{t('exitOffer.thenPerWeek', { weekly })}</p>
                    </div>

                    <p className="text-xs text-text-secondary text-center mb-2">{t('exitOffer.cancelAnytime')}</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); void handleSubscribe('overlay', e); }}
                      disabled={isLoading}
                      className="w-full py-3.5 mb-1 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {t('exitOffer.claimCta')}
                    </button>

                    <p className="text-xs text-text-secondary text-center mt-2 mb-4">{t('exitOffer.finePrint', { price: first, weekly })}</p>

                    {error && (
                      <div className="mb-4 p-3 bg-status-error/10 border border-status-error/20 rounded-xl flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-status-error flex-shrink-0" />
                        <p className="text-sm text-status-error">{error}</p>
                      </div>
                    )}

                    <CheckoutTrustStrip />
                    <PlanFeatureBullets credits={PRO_PLAN.credits} videoCount={10} isMax={false} />
                  </div>
                </div>
              </div>

              {/* Reviews — identical to the paywall */}
              <ReviewsSection />

              {/* FAQ — identical to the paywall */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-text-primary mb-4 text-center">{t('subscription.faq', { ns: 'translation' })}</h2>
                <div className="space-y-2 max-w-2xl mx-auto">
                  {FAQS.map((faq) => (
                    <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
                  ))}
                </div>
              </div>

              {/* Terms */}
              <div className="text-center">
                <p className="text-xs text-text-tertiary">
                  <a href="https://vivideo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-secondary">{t('exitOffer.terms')}</a>
                  {' · '}
                  <a href="https://vivideo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-secondary">{t('exitOffer.privacy')}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
